import { lazy, ComponentType } from 'react';
import { getComponentsForRouteFromRegistry } from './routeComponentsRegistry';

// Lazy load components for better performance
const TeamActivityFeed = lazy(() => import('@/components/dashboard/TeamActivityFeed'));
const TasksAssignedGrid = lazy(() => import('@/components/dashboard/TasksAssignedGrid'));
const TeamAvatars = lazy(() => import('@/components/dashboard/TeamAvatars'));
const TeamOnVacation = lazy(() => import('@/components/dashboard/TeamOnVacation'));
const ActiveBrands = lazy(() => import('@/components/dashboard/ActiveBrands'));
const TasksDueToday = lazy(() => import('@/components/dashboard/TasksDueToday'));
const TasksCompletedToday = lazy(() => import('@/components/dashboard/TasksCompletedToday'));
const ProjectProgress = lazy(() => import('@/components/dashboard/ProjectProgress'));
const ProjectProgressScore = lazy(() => import('@/components/dashboard/ProjectProgressScore'));
const LeadsSummary = lazy(() => import('@/components/dashboard/LeadsSummary'));
const WeeklyCalendarView = lazy(() => import('@/components/dashboard/WeeklyCalendarView').then(m => ({ default: m.WeeklyCalendarView })));
const VisualsToBeApproved = lazy(() => import('@/components/dashboard/VisualsToBeApproved').then(m => ({ default: m.VisualsToBeApproved })));
const UpcomingAppointments = lazy(() => import('@/components/dashboard/UpcomingAppointments').then(m => ({ default: m.UpcomingAppointments })));
const FteTasksList = lazy(() => import('@/components/dashboard/FteTasksList'));
const ConversionFunnel = lazy(() => import('@/components/dashboard/ConversionFunnel'));
const QuotaOverview = lazy(() => import('@/components/quota/QuotaStatusCard').then(m => ({ default: m.QuotaOverview })));

// Analytics components
const AnalyticsSessionsCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionsCard').then(m => ({ default: m.AnalyticsSessionsCard })));
const AnalyticsUsersCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsUsersCard').then(m => ({ default: m.AnalyticsUsersCard })));
const AnalyticsPageviewsCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsPageviewsCard').then(m => ({ default: m.AnalyticsPageviewsCard })));
const AnalyticsBounceRateCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsBounceRateCard').then(m => ({ default: m.AnalyticsBounceRateCard })));
const AnalyticsAvgSessionDurationCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsAvgSessionDurationCard').then(m => ({ default: m.AnalyticsAvgSessionDurationCard })));
const AnalyticsTrueTrafficCard = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrueTrafficCard').then(m => ({ default: m.AnalyticsTrueTrafficCard })));
const AnalyticsTrafficTrendsChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficTrendsChart').then(m => ({ default: m.AnalyticsTrafficTrendsChart })));
const AnalyticsDeviceSplitChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsDeviceSplitChart').then(m => ({ default: m.AnalyticsDeviceSplitChart })));
const AnalyticsTrafficDriversChart = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficDriversChart').then(m => ({ default: m.AnalyticsTrafficDriversChart })));
const AnalyticsTopPagesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTopPagesTable').then(m => ({ default: m.AnalyticsTopPagesTable })));
const AnalyticsTrafficSourcesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficSourcesTable').then(m => ({ default: m.AnalyticsTrafficSourcesTable })));
const AnalyticsTopReferrersTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTopReferrersTable').then(m => ({ default: m.AnalyticsTopReferrersTable })));
const AnalyticsHostnameTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsHostnameTable').then(m => ({ default: m.AnalyticsHostnameTable })));
const AnalyticsSessionSourcesTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionSourcesTable').then(m => ({ default: m.AnalyticsSessionSourcesTable })));
const AnalyticsSessionMediumTable = lazy(() => import('@/components/holdco-views/analytics/AnalyticsSessionMediumTable').then(m => ({ default: m.AnalyticsSessionMediumTable })));
const AnalyticsTrafficCountriesGrid = lazy(() => import('@/components/holdco-views/analytics/AnalyticsTrafficCountriesGrid').then(m => ({ default: m.AnalyticsTrafficCountriesGrid })));

// Route-specific: Scale 2Y3X, SEO Dashboard, SEO Report
const Scale2Y3XFlaggedIdeas = lazy(() => import('@/components/holdco-views/Scale2Y3XFlaggedIdeas').then(m => ({ default: m.Scale2Y3XFlaggedIdeas })));
const SeoDashboardOverviewCards = lazy(() => import('@/components/holdco-views/SeoDashboardOverviewCards').then(m => ({ default: m.SeoDashboardOverviewCards })));
const SeoReportQueryCountingCard = lazy(() => import('@/components/holdco-views/seo-report/SeoReportQueryCountingCard').then(m => ({ default: m.SeoReportQueryCountingCard })));
const SeoReportBrandedVsNonBrandedCard = lazy(() => import('@/components/holdco-views/seo-report/SeoReportBrandedVsNonBrandedCard').then(m => ({ default: m.SeoReportBrandedVsNonBrandedCard })));
const SeoReportGSCMonthlyChart = lazy(() => import('@/components/holdco-views/seo-report/SeoReportGSCMonthlyChart').then(m => ({ default: m.SeoReportGSCMonthlyChart })));

// Sales Insights components
const SalesInsightsTotalProposalsCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalProposalsCard').then(m => ({ default: m.SalesInsightsTotalProposalsCard })));
const SalesInsightsDraftCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsDraftCard').then(m => ({ default: m.SalesInsightsDraftCard })));
const SalesInsightsRespondedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsRespondedCard').then(m => ({ default: m.SalesInsightsRespondedCard })));
const SalesInsightsAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAwardedCard').then(m => ({ default: m.SalesInsightsAwardedCard })));
const SalesInsightsTotalValueAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalValueAwardedCard').then(m => ({ default: m.SalesInsightsTotalValueAwardedCard })));
const SalesInsightsTotalValueCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsTotalValueCard').then(m => ({ default: m.SalesInsightsTotalValueCard })));
const SalesInsightsAvgViewsPerProposalCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgViewsPerProposalCard').then(m => ({ default: m.SalesInsightsAvgViewsPerProposalCard })));
const SalesInsightsAvgViewsPerAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgViewsPerAwardedCard').then(m => ({ default: m.SalesInsightsAvgViewsPerAwardedCard })));
const SalesInsightsAwardedVsRespondedChart = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAwardedVsRespondedChart').then(m => ({ default: m.SalesInsightsAwardedVsRespondedChart })));
const SalesInsightsRespondedVsTotalChart = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsRespondedVsTotalChart').then(m => ({ default: m.SalesInsightsRespondedVsTotalChart })));
const SalesInsightsAvgDealValueAwardedCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgDealValueAwardedCard').then(m => ({ default: m.SalesInsightsAvgDealValueAwardedCard })));
const SalesInsightsAvgSalesCycleCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsAvgSalesCycleCard').then(m => ({ default: m.SalesInsightsAvgSalesCycleCard })));
const SalesInsightsWeightedAvgDealCard = lazy(() => import('@/components/holdco-views/sales-insights/SalesInsightsWeightedAvgDealCard').then(m => ({ default: m.SalesInsightsWeightedAvgDealCard })));

// Contacts (/contacts) holdco cards
const ContactsTotalContactsCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalContactsCard').then(m => ({ default: m.ContactsTotalContactsCard })));
const ContactsTotalOrganizationsCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalOrganizationsCard').then(m => ({ default: m.ContactsTotalOrganizationsCard })));
const ContactsTotalModulesCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalModulesCard').then(m => ({ default: m.ContactsTotalModulesCard })));
const ContactsTotalActivitiesCard = lazy(() => import('@/components/holdco-views/contacts/ContactsTotalActivitiesCard').then(m => ({ default: m.ContactsTotalActivitiesCard })));

// User Activity (/admin/user-activity-tracking) holdco cards
const UserActivityTotalTimeCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTotalTimeCard').then(m => ({ default: m.UserActivityTotalTimeCard })));
const UserActivityTotalVisitsCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTotalVisitsCard').then(m => ({ default: m.UserActivityTotalVisitsCard })));
const UserActivityUniqueRoutesCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityUniqueRoutesCard').then(m => ({ default: m.UserActivityUniqueRoutesCard })));
const UserActivityUniqueUsersCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityUniqueUsersCard').then(m => ({ default: m.UserActivityUniqueUsersCard })));
const UserActivityOverTimeChartCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityOverTimeChartCard').then(m => ({ default: m.UserActivityOverTimeChartCard })));
const UserActivityTimeByBrandChartCard = lazy(() => import('@/components/holdco-views/user-activity/UserActivityTimeByBrandChartCard').then(m => ({ default: m.UserActivityTimeByBrandChartCard })));

// User Time (/admin/user-time-tracking) holdco cards
const UserTimeTotalMinutesCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeTotalMinutesCard').then(m => ({ default: m.UserTimeTotalMinutesCard })));
const UserTimeFoundationalCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeFoundationalCard').then(m => ({ default: m.UserTimeFoundationalCard })));
const UserTimeGrowthCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeGrowthCard').then(m => ({ default: m.UserTimeGrowthCard })));
const UserTimeRecurringCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeRecurringCard').then(m => ({ default: m.UserTimeRecurringCard })));
const UserTimeLoggedOverTimeChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeLoggedOverTimeChartCard').then(m => ({ default: m.UserTimeLoggedOverTimeChartCard })));
const UserTimeByTaskTypeChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeByTaskTypeChartCard').then(m => ({ default: m.UserTimeByTaskTypeChartCard })));
const UserTimeBySourceChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeBySourceChartCard').then(m => ({ default: m.UserTimeBySourceChartCard })));
const UserTimeByBrandChartCard = lazy(() => import('@/components/holdco-views/user-time/UserTimeByBrandChartCard').then(m => ({ default: m.UserTimeByBrandChartCard })));

// Growth Standup (/growth-plan) - Day Start / Day End
const GrowthStandupDayStartCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayStartCard').then(m => ({ default: m.GrowthStandupDayStartCard })));
const GrowthStandupDayEndCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayEndCard').then(m => ({ default: m.GrowthStandupDayEndCard })));
const GrowthStandupTodaySummaryCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupTodaySummaryCard').then(m => ({ default: m.GrowthStandupTodaySummaryCard })));
const GrowthStandupDayStartOnlyCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayStartOnlyCard').then(m => ({ default: m.GrowthStandupDayStartOnlyCard })));
const GrowthStandupDayEndOnlyCard = lazy(() => import('@/components/holdco-views/growth-standup/GrowthStandupDayEndOnlyCard').then(m => ({ default: m.GrowthStandupDayEndOnlyCard })));

export interface DashboardComponentConfig {
  key: string;
  label: string;
  component: ComponentType<any>;
  defaultVisible: boolean;
  defaultOrder: number;
  category: 'header' | 'metrics' | 'insights' | 'activity' | 'calendar' | 'quota';
  gridConfig?: {
    colSpan?: number;
    lgColSpan?: number;
  };
  requiresBrandId?: boolean;
  requiresQuotaStatus?: boolean;
  /** If false, component is hidden from holdco view Configure Dashboard and defaults. Omit or true = available. */
  availableInHoldco?: boolean;
}

/**
 * Components that can be shown in holdco view dashboard.
 * Only includes DASHBOARD_COMPONENTS (which have React components).
 * Route-specific components are handled separately in the config dialog but not included here
 * since they don't have React components yet.
 * @param dbSettings - Optional map from DB (component_key -> available_in_holdco). 
 *   If a component is explicitly set to false in dbSettings, it's excluded.
 *   Otherwise, dashboard components are included by default.
 */
export const getHoldcoAvailableComponents = (
  dbSettings?: Record<string, boolean>
): DashboardComponentConfig[] => {
  // Only return dashboard components (which have React components)
  // Route-specific components will show in the config dialog but won't be included here
  // until we add React components for them
  return DASHBOARD_COMPONENTS.filter((c) => {
    // If component is explicitly disabled in dbSettings, exclude it
    if (dbSettings && c.key in dbSettings && dbSettings[c.key] === false) {
      return false;
    }
    // Otherwise include if availableInHoldco is not explicitly false
    return c.availableInHoldco !== false;
  });
};

export const DASHBOARD_COMPONENTS: DashboardComponentConfig[] = [
  {
    key: 'team_avatars',
    label: 'Team Avatars',
    component: TeamAvatars,
    defaultVisible: true,
    defaultOrder: 0,
    category: 'header',
  },
  {
    key: 'team_on_vacation',
    label: 'Team on Vacation',
    component: TeamOnVacation,
    defaultVisible: true,
    defaultOrder: 0.5,
    category: 'header',
  },
  {
    key: 'fte_tasks_list',
    label: 'FTE Tasks List',
    component: FteTasksList,
    defaultVisible: true,
    defaultOrder: 1,
    category: 'activity',
  },
  {
    key: 'upcoming_appointments',
    label: 'Upcoming Appointments',
    component: UpcomingAppointments,
    defaultVisible: true,
    defaultOrder: 2,
    category: 'calendar',
    requiresBrandId: true,
  },
  {
    key: 'active_brands',
    label: 'Active Brands',
    component: ActiveBrands,
    defaultVisible: true,
    defaultOrder: 3,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'tasks_due_today',
    label: 'Tasks Due Today',
    component: TasksDueToday,
    defaultVisible: true,
    defaultOrder: 4,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'tasks_completed_today',
    label: 'Tasks Completed Today',
    component: TasksCompletedToday,
    defaultVisible: true,
    defaultOrder: 5,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
  },
  {
    key: 'leads_summary',
    label: 'Leads Summary',
    component: LeadsSummary,
    defaultVisible: true,
    defaultOrder: 6,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'conversion_funnel',
    label: 'Conversion Funnel',
    component: ConversionFunnel,
    defaultVisible: true,
    defaultOrder: 7,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'project_progress',
    label: 'Growth Playbook Progress',
    component: ProjectProgress,
    defaultVisible: true,
    defaultOrder: 8,
    category: 'insights',
    gridConfig: { lgColSpan: 2 },
  },
  {
    key: 'project_progress_score',
    label: 'Progress Score',
    component: ProjectProgressScore,
    defaultVisible: true,
    defaultOrder: 9,
    category: 'insights',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'team_activity_feed',
    label: 'Team Activity Feed',
    component: TeamActivityFeed,
    defaultVisible: true,
    defaultOrder: 10,
    category: 'activity',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'tasks_assigned_grid',
    label: 'Tasks Assigned Grid',
    component: TasksAssignedGrid,
    defaultVisible: true,
    defaultOrder: 11,
    category: 'activity',
    gridConfig: { lgColSpan: 1 },
  },
  {
    key: 'visuals_to_be_approved',
    label: 'Visuals to be Approved',
    component: VisualsToBeApproved,
    defaultVisible: true,
    defaultOrder: 12,
    category: 'activity',
  },
  {
    key: 'weekly_calendar_view',
    label: 'Weekly Calendar View',
    component: WeeklyCalendarView,
    defaultVisible: true,
    defaultOrder: 13,
    category: 'calendar',
  },
  {
    key: 'quota_overview',
    label: 'Monthly Quotas',
    component: QuotaOverview,
    defaultVisible: true,
    defaultOrder: 14,
    category: 'quota',
    requiresQuotaStatus: true,
  },
  // Analytics metric cards
  {
    key: 'analytics_sessions',
    label: 'Sessions',
    component: AnalyticsSessionsCard,
    defaultVisible: false,
    defaultOrder: 100,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_users',
    label: 'Users',
    component: AnalyticsUsersCard,
    defaultVisible: false,
    defaultOrder: 101,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_pageviews',
    label: 'Page Views',
    component: AnalyticsPageviewsCard,
    defaultVisible: false,
    defaultOrder: 102,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_bounce_rate',
    label: 'Bounce Rate',
    component: AnalyticsBounceRateCard,
    defaultVisible: false,
    defaultOrder: 103,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_avg_session_duration',
    label: 'Avg Session Duration',
    component: AnalyticsAvgSessionDurationCard,
    defaultVisible: false,
    defaultOrder: 104,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  {
    key: 'analytics_true_traffic',
    label: 'True Traffic (Users)',
    component: AnalyticsTrueTrafficCard,
    defaultVisible: false,
    defaultOrder: 105,
    category: 'metrics',
    gridConfig: { colSpan: 1 },
    requiresBrandId: true,
  },
  // Analytics charts
  {
    key: 'analytics_traffic_trends_chart',
    label: 'Traffic Trends Chart',
    component: AnalyticsTrafficTrendsChart,
    defaultVisible: false,
    defaultOrder: 200,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_device_split_chart',
    label: 'Device Split Chart',
    component: AnalyticsDeviceSplitChart,
    defaultVisible: false,
    defaultOrder: 201,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_drivers_chart',
    label: 'Traffic Drivers Chart',
    component: AnalyticsTrafficDriversChart,
    defaultVisible: false,
    defaultOrder: 202,
    category: 'insights',
    requiresBrandId: true,
  },
  // Analytics tables
  {
    key: 'analytics_top_pages_table',
    label: 'Top-20 Pages Table',
    component: AnalyticsTopPagesTable,
    defaultVisible: false,
    defaultOrder: 300,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_sources_table',
    label: 'Traffic Sources Table',
    component: AnalyticsTrafficSourcesTable,
    defaultVisible: false,
    defaultOrder: 301,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_top_referrers_table',
    label: 'Top-20 Referrers Table',
    component: AnalyticsTopReferrersTable,
    defaultVisible: false,
    defaultOrder: 302,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_hostname_table',
    label: 'Traffic by Hostname Table',
    component: AnalyticsHostnameTable,
    defaultVisible: false,
    defaultOrder: 303,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_session_sources_table',
    label: 'Top-20 Session Sources Table',
    component: AnalyticsSessionSourcesTable,
    defaultVisible: false,
    defaultOrder: 304,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_session_medium_table',
    label: 'Top-20 Session Medium Table',
    component: AnalyticsSessionMediumTable,
    defaultVisible: false,
    defaultOrder: 305,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'analytics_traffic_countries_grid',
    label: 'Traffic Countries Grid',
    component: AnalyticsTrafficCountriesGrid,
    defaultVisible: false,
    defaultOrder: 306,
    category: 'insights',
    requiresBrandId: true,
  },
  // Scale 2Y3X Dashboard
  {
    key: 'scale_2y3x_flagged_ideas',
    label: 'Flagged Ideas to Revisit',
    component: Scale2Y3XFlaggedIdeas,
    defaultVisible: false,
    defaultOrder: 400,
    category: 'activity',
    requiresBrandId: true,
  },
  // SEO Dashboard
  {
    key: 'seo_dashboard_overview_cards',
    label: 'SEO Overview Cards',
    component: SeoDashboardOverviewCards,
    defaultVisible: false,
    defaultOrder: 401,
    category: 'metrics',
    requiresBrandId: true,
  },
  // SEO Report
  {
    key: 'seo_report_query_counting',
    label: 'Query Counting Card',
    component: SeoReportQueryCountingCard,
    defaultVisible: false,
    defaultOrder: 402,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'seo_report_branded_vs_nonbranded',
    label: 'Branded vs Non-Branded',
    component: SeoReportBrandedVsNonBrandedCard,
    defaultVisible: false,
    defaultOrder: 403,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'seo_report_gsc_monthly_chart',
    label: 'GSC Monthly Chart',
    component: SeoReportGSCMonthlyChart,
    defaultVisible: false,
    defaultOrder: 404,
    category: 'insights',
    requiresBrandId: true,
  },
  // Sales Insights
  {
    key: 'sales_insights_total_proposals',
    label: 'Total Proposals',
    component: SalesInsightsTotalProposalsCard,
    defaultVisible: false,
    defaultOrder: 500,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_draft',
    label: 'Draft Proposals',
    component: SalesInsightsDraftCard,
    defaultVisible: false,
    defaultOrder: 501,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_responded',
    label: 'Responded Proposals',
    component: SalesInsightsRespondedCard,
    defaultVisible: false,
    defaultOrder: 502,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_awarded',
    label: 'Awarded Proposals',
    component: SalesInsightsAwardedCard,
    defaultVisible: false,
    defaultOrder: 503,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_total_value_awarded',
    label: 'Total Value Awarded',
    component: SalesInsightsTotalValueAwardedCard,
    defaultVisible: false,
    defaultOrder: 504,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_total_value',
    label: 'Total Value',
    component: SalesInsightsTotalValueCard,
    defaultVisible: false,
    defaultOrder: 505,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_views_per_proposal',
    label: 'Avg Views per Proposal',
    component: SalesInsightsAvgViewsPerProposalCard,
    defaultVisible: false,
    defaultOrder: 506,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_views_per_awarded',
    label: 'Avg Views per Awarded',
    component: SalesInsightsAvgViewsPerAwardedCard,
    defaultVisible: false,
    defaultOrder: 507,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_awarded_vs_responded_chart',
    label: 'Awarded vs Responded Chart',
    component: SalesInsightsAwardedVsRespondedChart,
    defaultVisible: false,
    defaultOrder: 508,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_responded_vs_total_chart',
    label: 'Responded vs Total Chart',
    component: SalesInsightsRespondedVsTotalChart,
    defaultVisible: false,
    defaultOrder: 509,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_deal_value_awarded',
    label: 'Avg $ Deal Awarded',
    component: SalesInsightsAvgDealValueAwardedCard,
    defaultVisible: false,
    defaultOrder: 510,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_avg_sales_cycle',
    label: 'Avg Sales Cycle',
    component: SalesInsightsAvgSalesCycleCard,
    defaultVisible: false,
    defaultOrder: 511,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'sales_insights_weighted_avg_deal',
    label: 'Weighted Avg $ Deal',
    component: SalesInsightsWeightedAvgDealCard,
    defaultVisible: false,
    defaultOrder: 512,
    category: 'metrics',
    requiresBrandId: true,
  },
  // Contacts (/contacts)
  {
    key: 'contacts_total_contacts',
    label: 'Total Contacts',
    component: ContactsTotalContactsCard,
    defaultVisible: false,
    defaultOrder: 600,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_organizations',
    label: 'Total Organizations',
    component: ContactsTotalOrganizationsCard,
    defaultVisible: false,
    defaultOrder: 601,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_modules',
    label: 'Total Modules',
    component: ContactsTotalModulesCard,
    defaultVisible: false,
    defaultOrder: 602,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'contacts_total_activities',
    label: 'Total Activities',
    component: ContactsTotalActivitiesCard,
    defaultVisible: false,
    defaultOrder: 603,
    category: 'metrics',
    requiresBrandId: true,
  },
  // User Activity (/admin/user-activity-tracking)
  {
    key: 'user_activity_total_time',
    label: 'Total Time',
    component: UserActivityTotalTimeCard,
    defaultVisible: false,
    defaultOrder: 700,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_total_visits',
    label: 'Total Visits',
    component: UserActivityTotalVisitsCard,
    defaultVisible: false,
    defaultOrder: 701,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_unique_routes',
    label: 'Unique Routes',
    component: UserActivityUniqueRoutesCard,
    defaultVisible: false,
    defaultOrder: 702,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_unique_users',
    label: 'Unique Users',
    component: UserActivityUniqueUsersCard,
    defaultVisible: false,
    defaultOrder: 703,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_over_time_chart',
    label: 'Activity Over Time',
    component: UserActivityOverTimeChartCard,
    defaultVisible: false,
    defaultOrder: 704,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_activity_time_by_brand_chart',
    label: 'Time by Brand',
    component: UserActivityTimeByBrandChartCard,
    defaultVisible: false,
    defaultOrder: 705,
    category: 'insights',
    requiresBrandId: true,
  },
  // User Time (/admin/user-time-tracking)
  {
    key: 'user_time_total_minutes',
    label: 'Total Time',
    component: UserTimeTotalMinutesCard,
    defaultVisible: false,
    defaultOrder: 710,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_foundational',
    label: 'Foundational',
    component: UserTimeFoundationalCard,
    defaultVisible: false,
    defaultOrder: 711,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_growth',
    label: 'Growth',
    component: UserTimeGrowthCard,
    defaultVisible: false,
    defaultOrder: 712,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_recurring',
    label: 'Recurring',
    component: UserTimeRecurringCard,
    defaultVisible: false,
    defaultOrder: 713,
    category: 'metrics',
    requiresBrandId: true,
  },
  {
    key: 'user_time_logged_over_time_chart',
    label: 'Time Logged Over Time',
    component: UserTimeLoggedOverTimeChartCard,
    defaultVisible: false,
    defaultOrder: 714,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_task_type_chart',
    label: 'By Task Type',
    component: UserTimeByTaskTypeChartCard,
    defaultVisible: false,
    defaultOrder: 715,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_source_chart',
    label: 'By Source',
    component: UserTimeBySourceChartCard,
    defaultVisible: false,
    defaultOrder: 716,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'user_time_by_brand_chart',
    label: 'By Brand',
    component: UserTimeByBrandChartCard,
    defaultVisible: false,
    defaultOrder: 717,
    category: 'insights',
    requiresBrandId: true,
  },
  {
    key: 'growth_standup_day_start',
    label: 'Day Start',
    component: GrowthStandupDayStartCard,
    defaultVisible: false,
    defaultOrder: 720,
    category: 'activity',
  },
  {
    key: 'growth_standup_day_end',
    label: 'Day End',
    component: GrowthStandupDayEndCard,
    defaultVisible: false,
    defaultOrder: 721,
    category: 'activity',
  },
  {
    key: 'growth_standup_today_summary',
    label: "Today's Standup Summary",
    component: GrowthStandupTodaySummaryCard,
    defaultVisible: false,
    defaultOrder: 722,
    category: 'metrics',
  },
  {
    key: 'growth_standup_day_start_only',
    label: 'Day Start (separate)',
    component: GrowthStandupDayStartOnlyCard,
    defaultVisible: false,
    defaultOrder: 723,
    category: 'activity',
  },
  {
    key: 'growth_standup_day_end_only',
    label: 'Day End (separate)',
    component: GrowthStandupDayEndOnlyCard,
    defaultVisible: false,
    defaultOrder: 724,
    category: 'activity',
  },
];

export const getDefaultConfig = () =>
  DASHBOARD_COMPONENTS.map((c) => ({
    component_key: c.key,
    is_visible: c.defaultVisible,
    display_order: c.defaultOrder,
    grid_column: null as number | null,
  }));

/**
 * Custom components for routes that aren't in DASHBOARD_COMPONENTS.
 * Used for label/category lookup when rendering config dialogs.
 */
export const ROUTE_SPECIFIC_COMPONENTS: Array<{
  key: string;
  label: string;
  category: string;
}> = [
  // Analytics
  { key: 'analytics_sessions', label: 'Sessions', category: 'metrics' },
  { key: 'analytics_users', label: 'Users', category: 'metrics' },
  { key: 'analytics_pageviews', label: 'Page Views', category: 'metrics' },
  { key: 'analytics_bounce_rate', label: 'Bounce Rate', category: 'metrics' },
  { key: 'analytics_avg_session_duration', label: 'Avg Session Duration', category: 'metrics' },
  { key: 'analytics_true_traffic', label: 'True Traffic (Users)', category: 'metrics' },
  { key: 'analytics_traffic_trends_chart', label: 'Traffic Trends Chart', category: 'insights' },
  { key: 'analytics_device_split_chart', label: 'Device Split Chart', category: 'insights' },
  { key: 'analytics_traffic_drivers_chart', label: 'Traffic Drivers Chart', category: 'insights' },
  { key: 'analytics_top_pages_table', label: 'Top-20 Pages Table', category: 'insights' },
  { key: 'analytics_traffic_sources_table', label: 'Traffic Sources Table', category: 'insights' },
  { key: 'analytics_top_referrers_table', label: 'Top-20 Referrers Table', category: 'insights' },
  { key: 'analytics_hostname_table', label: 'Traffic by Hostname Table', category: 'insights' },
  { key: 'analytics_session_sources_table', label: 'Top-20 Session Sources Table', category: 'insights' },
  { key: 'analytics_session_medium_table', label: 'Top-20 Session Medium Table', category: 'insights' },
  { key: 'analytics_traffic_countries_grid', label: 'Traffic Countries Grid', category: 'insights' },
  // SEO Report
  { key: 'seo_report_query_counting', label: 'Query Counting Card', category: 'metrics' },
  { key: 'seo_report_branded_vs_nonbranded', label: 'Branded vs Non-Branded', category: 'metrics' },
  { key: 'seo_report_gsc_monthly_chart', label: 'GSC Monthly Chart', category: 'insights' },
  { key: 'seo_report_top_pages', label: 'Top Pages Table', category: 'insights' },
  { key: 'seo_report_top_queries', label: 'Top Queries Table', category: 'insights' },
  { key: 'seo_report_countries', label: 'Countries Pie Chart', category: 'insights' },
  { key: 'seo_report_devices', label: 'Devices Donut Chart', category: 'insights' },
  { key: 'seo_report_declines', label: 'Decline Analysis', category: 'insights' },
  { key: 'seo_report_increases', label: 'Increase Analysis', category: 'insights' },
  // SEO Dashboard
  { key: 'seo_dashboard_overview_cards', label: 'SEO Overview Cards', category: 'metrics' },
  { key: 'seo_dashboard_stats_cards', label: 'SEO Stats Cards', category: 'metrics' },
  { key: 'seo_dashboard_keyword_sources', label: 'Keyword Sources Cards', category: 'insights' },
  { key: 'seo_dashboard_on_page_section', label: 'On-Page SEO Section', category: 'insights' },
  { key: 'seo_dashboard_top_countries', label: 'Top Countries Card', category: 'insights' },
  { key: 'seo_dashboard_keyword_opportunities', label: 'Keyword Opportunities Chart', category: 'insights' },
  { key: 'seo_dashboard_recent_activity', label: 'Recent Activity Card', category: 'activity' },
  // Scale 2Y3X Dashboard
  { key: 'scale_2y3x_team_avatars', label: 'Team Avatars', category: 'header' },
  { key: 'scale_2y3x_goals_card', label: 'Goals Card', category: 'metrics' },
  { key: 'scale_2y3x_growth_lab_team_card', label: 'Growth Lab Team Card', category: 'metrics' },
  { key: 'scale_2y3x_active_sprints_card', label: 'Active Sprints Card', category: 'metrics' },
  { key: 'scale_2y3x_ideas_card', label: 'Ideas Card', category: 'metrics' },
  { key: 'scale_2y3x_flagged_ideas', label: 'Flagged Ideas to Revisit', category: 'activity' },
  { key: 'scale_2y3x_time_progress', label: 'Time Progress', category: 'insights' },
  { key: 'scale_2y3x_qualitative_task_progress', label: 'Qualitative Task Progress', category: 'insights' },
  { key: 'scale_2y3x_goal_progress', label: 'Goal Progress', category: 'insights' },
  { key: 'scale_2y3x_team_members_list', label: 'Team Members List', category: 'activity' },
  // Sales Insights
  { key: 'sales_insights_total_proposals', label: 'Total Proposals', category: 'metrics' },
  { key: 'sales_insights_draft', label: 'Draft Proposals', category: 'metrics' },
  { key: 'sales_insights_responded', label: 'Responded Proposals', category: 'metrics' },
  { key: 'sales_insights_awarded', label: 'Awarded Proposals', category: 'metrics' },
  { key: 'sales_insights_total_value_awarded', label: 'Total Value Awarded', category: 'metrics' },
  { key: 'sales_insights_total_value', label: 'Total Value', category: 'metrics' },
  { key: 'sales_insights_avg_views_per_proposal', label: 'Avg Views per Proposal', category: 'metrics' },
  { key: 'sales_insights_avg_views_per_awarded', label: 'Avg Views per Awarded', category: 'metrics' },
  { key: 'sales_insights_awarded_vs_responded_chart', label: 'Awarded vs Responded Chart', category: 'insights' },
  { key: 'sales_insights_responded_vs_total_chart', label: 'Responded vs Total Chart', category: 'insights' },
  { key: 'sales_insights_proposals_table', label: 'Proposals Table', category: 'insights' },
  // Contacts
  { key: 'contacts_total_contacts', label: 'Total Contacts', category: 'metrics' },
  { key: 'contacts_total_organizations', label: 'Total Organizations', category: 'metrics' },
  { key: 'contacts_total_modules', label: 'Total Modules', category: 'metrics' },
  { key: 'contacts_total_activities', label: 'Total Activities', category: 'metrics' },
  { key: 'contacts_module_filters', label: 'Module Filters', category: 'header' },
  { key: 'contacts_activity_filters', label: 'Activity Filters', category: 'header' },
  { key: 'contacts_search', label: 'Search', category: 'header' },
  { key: 'contacts_table', label: 'Contacts Table', category: 'activity' },
  // User Activity Tracking
  { key: 'user_activity_filters', label: 'Brand / User / Date Filters', category: 'header' },
  { key: 'user_activity_total_time', label: 'Total Time', category: 'metrics' },
  { key: 'user_activity_total_visits', label: 'Total Visits', category: 'metrics' },
  { key: 'user_activity_unique_routes', label: 'Unique Routes', category: 'metrics' },
  { key: 'user_activity_unique_users', label: 'Unique Users', category: 'metrics' },
  { key: 'user_activity_over_time_chart', label: 'Activity Over Time', category: 'insights' },
  { key: 'user_activity_time_by_brand_chart', label: 'Time by Brand', category: 'insights' },
  { key: 'user_activity_records_table', label: 'Activity Records Table', category: 'activity' },
  // User Time Tracking
  { key: 'user_time_filters', label: 'Brand / User / Date / Entry Type Filters', category: 'header' },
  { key: 'user_time_total_minutes', label: 'Total Time', category: 'metrics' },
  { key: 'user_time_foundational', label: 'Foundational', category: 'metrics' },
  { key: 'user_time_growth', label: 'Growth', category: 'metrics' },
  { key: 'user_time_recurring', label: 'Recurring', category: 'metrics' },
  { key: 'user_time_logged_over_time_chart', label: 'Time Logged Over Time', category: 'insights' },
  { key: 'user_time_by_task_type_chart', label: 'By Task Type', category: 'insights' },
  { key: 'user_time_by_source_chart', label: 'By Source', category: 'insights' },
  { key: 'user_time_by_brand_chart', label: 'By Brand', category: 'insights' },
  { key: 'user_time_entries_table', label: 'Time Entries Table', category: 'activity' },
  // Growth Plan
  { key: 'growth_standup_day_start', label: 'Day Start', category: 'activity' },
  { key: 'growth_standup_day_end', label: 'Day End', category: 'activity' },
  { key: 'growth_standup_today_summary', label: "Today's Standup Summary", category: 'metrics' },
  { key: 'growth_standup_day_start_only', label: 'Day Start (separate)', category: 'activity' },
  { key: 'growth_standup_day_end_only', label: 'Day End (separate)', category: 'activity' },
  // Sales Insights (extra metrics when added via settings)
  { key: 'sales_insights_weighted_avg_deal', label: 'Weighted Avg Deal', category: 'metrics' },
  { key: 'sales_insights_avg_deal_value_awarded', label: 'Avg Deal Value Awarded', category: 'metrics' },
  { key: 'sales_insights_avg_sales_cycle', label: 'Avg Sales Cycle', category: 'metrics' },
];

/** Re-export registered routes for UI hints. */
export { REGISTERED_ROUTES } from './routeComponentsRegistry';

/** Get components for a route from the registry. Returns actual components ON that page. No pattern matching. */
export function getComponentsForRoute(route: string): Array<{
  key: string;
  label: string;
  category: string;
}> {
  return getComponentsForRouteFromRegistry(route);
}
